import { useUpdateUserSettings } from 'features/user';
import { useCallback } from 'react';
import { useUpdateTasksMutation } from 'shared/hooks/queries/use-update-tasks-mutation';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { ID } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';

export const useInboxOnReorder = (groupBy: GroupByMode | undefined | null) => {
  const updateSettings = useUpdateUserSettings();
  const { mutate: updateTasks } = useUpdateTasksMutation();
  const updateRelationships = useUpdateRelationshipsReferences();

  return useCallback(
    (
      updatedSections: { id: ID; taskSorting: ID[] }[],
      updatedTasks: Task[],
      relationshipUpdates?: RelationshipUpdate[],
    ) => {
      const groupByMode = groupBy ?? GroupByMode.None;
      // groupByMode none means there is just a single section
      const noGroupingSorting =
        updatedSections.length === 1 && groupByMode === GroupByMode.None
          ? updatedSections[0].taskSorting
          : undefined;
      const groupBySorting =
        noGroupingSorting ??
        updatedSections.reduce<Record<ID, ID[]>>((acc, section) => {
          acc[section.id] = section.taskSorting;
          return acc;
        }, {});

      updateSettings({
        filters: {
          inbox: {
            sortingMode: SortingMode.Custom,
          },
        },
        customTaskOrder: {
          inbox: {
            [groupByMode]: groupBySorting,
          },
        },
      });
      updateTasks(updatedTasks);

      // handle relationship updates if provided
      if (relationshipUpdates?.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [groupBy, updateSettings, updateTasks, updateRelationships],
  );
};
